import { defineQueries, defineQueryWithType, type Query } from '@rocicorp/zero';
import { z } from 'zod';
import { PUBLIC_ORGANIZATION_ID } from '../ent/root-entity-ids';
import { MAX_FILES_PER_PARENT } from '../files/constants';
import { schema } from '../zero-schema';
import {
  isChannelOrgMember,
  isEightySixAnalyticsViewer,
  isOrgMember,
  isOwnedByVisibleOrg,
  isOwnOrgRow,
  isOwnOrSharedOrgUserRow,
  isOwnPreferenceRow,
  isVisibleFileRow,
  isVisibleKnowledgeArticleRow,
  isVisibleMenuChildRow,
  isVisibleMenuRow,
  isVisibleUserContactRow,
  zb,
  type QueryCtx
} from './query-authz';

const defineAppQuery = defineQueryWithType<typeof schema, QueryCtx>();

const idSchema = z.string().trim().min(1).max(200);
/** Cap for the menu editor's "Add from library" picker. */
export const MENU_PICKER_LIMIT = 200;
const orgIdArgs = z.object({ organizationId: idSchema });

// The two fragments below are passed straight to `.related()`. `TReturn` is
// whatever row shape the relationship hands in (it carries the one/many
// cardinality), so each query's result type is the same as with the callback
// written inline.

/**
 * A parent row's attachments, capped at the shared per-parent limit:
 * `.related('files', cappedFiles)`.
 */
const cappedFiles = <TReturn>(files: Query<'files', typeof schema, TReturn>) =>
  files.limit(MAX_FILES_PER_PARENT);

/**
 * A related user plus their contact row, when the caller may see it:
 * `.related('user' | 'createdBy' | ..., withVisibleContact(ctx))`. Synced
 * queries bypass definePermissions, so the contact predicate has to travel
 * with every related user (see isVisibleUserContactRow).
 */
const withVisibleContact =
  (ctx: QueryCtx) =>
  <TReturn>(user: Query<'users', typeof schema, TReturn>) =>
    user.related('contact', contact =>
      contact.where(eb => isVisibleUserContactRow(ctx, eb))
    );

export const queries = defineQueries({
  users: {
    // Replaces z.query.users.where('clerkId', userId).one() -- a user is
    // always allowed to read their own row, no membership check needed.
    current: defineAppQuery(({ ctx }) =>
      zb.users.where('clerkId', ctx.sub).one()
    )
  },

  organizationUsers: {
    // Replaces the organizationUsers.where(...).where(...).one() lookup in
    // use-current-user.ts's useCurrentOrgUser.
    // Returns every role row for the caller (one per role); the hook picks
    // the most privileged so a promoted admin is not treated as an employee.
    currentMembership: defineAppQuery(orgIdArgs, ({ args, ctx }) =>
      zb.organizationUsers
        .where('organizationId', args.organizationId)
        .whereExists('user', uq => uq.where('clerkId', '=', ctx.sub))
    ),
    // Replaces UserListTable.tsx's organizationUsers-by-org listing.
    byOrg: defineAppQuery(orgIdArgs, ({ args, ctx }) =>
      zb.organizationUsers
        .where('organizationId', args.organizationId)
        .where(eb => isOwnOrSharedOrgUserRow(ctx, eb))
        .related('user', withVisibleContact(ctx))
    ),
    // Replaces EmployeeForm.tsx's single-employee lookup.
    byOrgAndUser: defineAppQuery(
      z.object({ organizationId: idSchema, userId: idSchema }),
      ({ args, ctx }) =>
        zb.organizationUsers
          .where('organizationId', args.organizationId)
          .where('userId', args.userId)
          .where(eb => isOwnOrSharedOrgUserRow(ctx, eb))
          .related('user', withVisibleContact(ctx))
    )
  },

  organizations: {
    // Search's organization eyebrow needs a display name; nothing else
    // reads a single organization's own row today.
    current: defineAppQuery(orgIdArgs, ({ args, ctx }) =>
      zb.organizations
        .where('id', args.organizationId)
        .where(eb => isOwnOrgRow(ctx, eb))
        .one()
    ),
    // Replaces ChatChannels.tsx's organizations->chatChannels lookup.
    withChannels: defineAppQuery(orgIdArgs, ({ args, ctx }) =>
      zb.organizations
        .where('id', args.organizationId)
        .where(eb => isOrgMember(ctx, eb))
        .related('chatChannels', (cq: any) =>
          cq
            .where((eb: any) => isChannelOrgMember(ctx, eb))
            .related('members', (mq: any) => mq.related('user'))
            .related('reads')
        )
        .one()
    ),
    // Replaces BevMenu.tsx's organizations->menuEdges->menu lookup. Menus
    // are per-org business data, not a public catalog -- isOwnOrgRow, no
    // PUBLIC_ORGANIZATION_ID exemption.
    withMenus: defineAppQuery(orgIdArgs, ({ args, ctx }) =>
      zb.organizations
        .where('id', args.organizationId)
        .where(eb => isOwnOrgRow(ctx, eb))
        .related('menuEdges', eq =>
          eq.related('menu', mq =>
            mq
              .related('files', cappedFiles)
              // LQD-6 card meta ("N items"): item rows only, filtered by
              // the menu-child predicate like every other related read.
              .related('items', iq =>
                iq.where(eb => isVisibleMenuChildRow(ctx, eb))
              )
          )
        )
        .one()
    )
  },

  chatChannels: {
    // Replaces ChatChannelMessages.tsx.
    withMessages: defineAppQuery(
      z.object({ channelId: idSchema }),
      ({ args, ctx }) =>
        zb.chatChannels
          .where('id', args.channelId)
          .where(eb => isChannelOrgMember(ctx, eb))
          // Nested .related() callback typed `any` -- Zero's relationship-
          // inference doesn't propagate the table literal through more than
          // one level of nesting (same limitation noted in query-authz.ts's
          // ported predicates).
          .related('chatMessages', (q: any) =>
            q.related('sender').related('files', cappedFiles)
          )
          .related('members', (mq: any) => mq.related('user'))
          .related('createdBy')
          .related('reads')
          .one()
    ),
    // Backs ChannelDetailsPanel: members + creator for any visible channel,
    // including one that is not the active conversation.
    details: defineAppQuery(
      z.object({ channelId: idSchema }),
      ({ args, ctx }) =>
        zb.chatChannels
          .where('id', args.channelId)
          .where(eb => isChannelOrgMember(ctx, eb))
          .related('members', (mq: any) => mq.related('user'))
          .related('createdBy')
          .one()
    ),
    search: defineAppQuery(
      z.object({
        organizationId: idSchema,
        term: z.string().trim().min(2).max(200)
      }),
      ({ args, ctx }) =>
        zb.chatChannels
          .where('organizationId', args.organizationId)
          .where('name', 'ILIKE', `%${args.term}%`)
          .where(eb => isChannelOrgMember(ctx, eb))
          .related('members', (mq: any) => mq.related('user'))
          .limit(8)
    )
  },

  recipes: {
    // Replaces RecipeEditor.tsx.
    withIngredients: defineAppQuery(
      z.object({ recipeId: idSchema }),
      ({ args, ctx }) =>
        zb.recipes
          .where('id', args.recipeId)
          .where(eb => isOwnedByVisibleOrg(ctx, eb))
          .related('ingredientLinks', rq =>
            rq.related('ingredient', iq =>
              iq
                .related('preparationRecipe', pq =>
                  pq.where(eb => isOwnedByVisibleOrg(ctx, eb))
                )
                .related('files', cappedFiles)
                // Whichever org actually owns this ingredient row -- not
                // filtered to the *page's* org, since the page you're
                // viewing a recipe from and the org that owns its
                // ingredients aren't always the same (e.g. browsing under
                // a different org's URL). A shared/seeded ingredient (e.g.
                // from the classic library) has no edge here at all, and
                // image generation's org-edge check would reject it, so
                // the UI uses "any row here" to hide the "Generate image"
                // control instead of offering one that fails on submit;
                // when there IS an owner, its id (not the page's org) is
                // what the edit/generate requests actually use -- the
                // server re-checks the caller's real membership in that
                // org either way.
                .related('viaOrgEdge')
            )
          )
          .related('files', cappedFiles)
          .one()
    ),
    // Backs the command-palette quick-jump (KnowledgeSearch.tsx) --
    // scoped to a specific org (not just "any visible org") since the
    // palette only ever searches the caller's current org.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    search: defineAppQuery(
      z.object({
        organizationId: idSchema,
        term: z.string().trim().min(2).max(200)
      }),
      ({ args }) =>
        zb.recipes
          .where('name', 'ILIKE', `%${args.term}%`)
          .where((eb: any) =>
            eb.exists('viaOrgEdge', (peq: any) =>
              peq.where('sourceId', args.organizationId)
            )
          )
          .limit(8)
    ),
    // Backs the menu editor's "Add from library" sheet: the org's own
    // recipes (org -> recipe edge from an org the caller belongs to), by
    // name. Which ones are already on the menu comes from menus.editor.
    forMenuPicker: defineAppQuery(
      z.object({
        organizationId: idSchema,
        term: z.string().trim().max(200).optional()
      }),
      ({ args, ctx }) => {
        let query = zb.recipes
          .where((eb: any) =>
            eb.exists('viaOrgEdge', (peq: any) =>
              peq
                .where('sourceId', args.organizationId)
                .whereExists('sourceOrganization', (oq: any) =>
                  oq.where((orgEb: any) => isOwnOrgRow(ctx, orgEb))
                )
            )
          )
          .orderBy('name', 'asc')
          .orderBy('id', 'asc')
          .limit(MENU_PICKER_LIMIT);
        if (args.term) {
          query = query.where('name', 'ILIKE', `%${args.term}%`);
        }
        return query;
      }
    )
  },
  ingredients: {
    // Backs the standalone ingredient sheet reached from search (edge case:
    // an ingredient shared by multiple recipes has no single parent
    // recipe/quantity, so this is read-only -- shows identity fields plus
    // which recipes use it).
    byId: defineAppQuery(
      z.object({ ingredientId: idSchema }),
      ({ args, ctx }) =>
        zb.ingredients
          .where('id', args.ingredientId)
          .related('preparationRecipe', pq =>
            pq.where(eb => isOwnedByVisibleOrg(ctx, eb))
          )
          .related('recipeLinks', rq => rq.related('recipe'))
          .related('files', cappedFiles)
          // See recipes.withIngredients: whichever org actually owns this
          // ingredient, not filtered to the page's org.
          .related('viaOrgEdge')
          .one()
    ),
    // Backs the command-palette quick-jump for ingredients shared across
    // recipes -- reachable via recipeLinks -> recipe -> viaOrgEdge, mirroring
    // the row-level permission in zero-schema.ts's `ingredients` select rule.
    search: defineAppQuery(
      z.object({
        organizationId: idSchema,
        term: z.string().trim().min(2).max(200)
      }),
      ({ args }) =>
        zb.ingredients
          .where('fullName', 'ILIKE', `%${args.term}%`)
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .where((eb: any) =>
            eb.exists('recipeLinks', (riq: any) =>
              riq.whereExists('recipe', (rq: any) =>
                rq.whereExists('viaOrgEdge', (peq: any) =>
                  peq.where('sourceId', args.organizationId)
                )
              )
            )
          )
          .related('recipeLinks', rq => rq.related('recipe'))
          .limit(8)
    ),
    // Retained 86-list catalog autocomplete. The edit form is free text now.
    // scoped to a specific org via the ingredient's own direct catalog
    // edge (not "used in one of my org's recipes" like `search` above),
    // since an 86-listable ingredient need not appear in any recipe yet.
    // Row-level visibility is enforced independently by zero-schema.ts's
    // `ingredients` select rule (isOwnedByVisibleOrg OR recipe-linked), so
    // this exists() is a query-shape narrowing, not the sole authorization
    // boundary.
    searchByOrgCatalog: defineAppQuery(
      z.object({
        organizationId: idSchema,
        term: z.string().trim().min(1).max(200)
      }),
      ({ args }) =>
        zb.ingredients
          .where('fullName', 'ILIKE', `%${args.term}%`)
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .where((eb: any) =>
            eb.exists('viaOrgEdge', (peq: any) =>
              peq.where('sourceId', args.organizationId)
            )
          )
          .limit(8)
    )
  },

  menus: {
    byId: defineAppQuery(z.object({ menuId: idSchema }), ({ args, ctx }) =>
      zb.menus
        .where('id', args.menuId)
        .where(eb =>
          eb.exists('viaOrgEdge', (peq: any) =>
            peq.whereExists('sourceOrganization', (oq: any) =>
              oq.whereExists('organizationUsers', (ouq: any) =>
                ouq.whereExists('user', (uq: any) =>
                  uq.where('clerkId', '=', ctx.sub)
                )
              )
            )
          )
        )
        .related('files', cappedFiles)
        .one()
    ),
    // LQD-6 menu editor and detail view: the menu, its files and cover,
    // ordered sections, each with its ordered items and their recipes.
    // custom queries bypass definePermissions, so every .related() carries
    // its table's predicate.
    editor: defineAppQuery(z.object({ menuId: idSchema }), ({ args, ctx }) =>
      zb.menus
        .where('id', args.menuId)
        .where(eb => isVisibleMenuRow(ctx, eb))
        .related('files', fq =>
          cappedFiles(fq.where(eb => isVisibleFileRow(ctx, eb)))
        )
        .related('coverFile', fq => fq.where(eb => isVisibleFileRow(ctx, eb)))
        .related('sections', sq =>
          sq
            .where(eb => isVisibleMenuChildRow(ctx, eb))
            .orderBy('position', 'asc')
            .orderBy('id', 'asc')
            .related('items', (iq: any) =>
              iq
                .where((eb: any) => isVisibleMenuChildRow(ctx, eb))
                .orderBy('position', 'asc')
                .orderBy('id', 'asc')
                .related('recipe', (rq: any) =>
                  rq.where((eb: any) => isOwnedByVisibleOrg(ctx, eb))
                )
            )
        )
        .one()
    ),
    search: defineAppQuery(
      z.object({
        organizationId: idSchema,
        term: z.string().trim().min(2).max(200)
      }),
      ({ args }) =>
        zb.menus
          .where('name', 'ILIKE', `%${args.term}%`)
          .where((eb: any) =>
            eb.exists('viaOrgEdge', (peq: any) =>
              peq.where('sourceId', args.organizationId)
            )
          )
          .limit(8)
    )
  },

  menuImports: {
    byId: defineAppQuery(z.object({ importId: idSchema }), ({ args, ctx }) =>
      zb.menuImports
        .where('id', args.importId)
        .where(eb =>
          eb.exists('organization', (oq: any) =>
            oq.whereExists('organizationUsers', (ouq: any) =>
              ouq.whereExists('user', (uq: any) =>
                uq.where('clerkId', '=', ctx.sub)
              )
            )
          )
        )
        .one()
    ),
    // A batch (zip / Drive / Photos) watches every import it started at once.
    byIds: defineAppQuery(
      z.object({ importIds: z.array(idSchema).min(1).max(50) }),
      ({ args, ctx }) =>
        zb.menuImports
          .where('id', 'IN', args.importIds)
          .where(eb =>
            eb.exists('organization', (oq: any) =>
              oq.whereExists('organizationUsers', (ouq: any) =>
                ouq.whereExists('user', (uq: any) =>
                  uq.where('clerkId', '=', ctx.sub)
                )
              )
            )
          )
    )
  },

  people: {
    search: defineAppQuery(
      z.object({
        organizationId: idSchema,
        term: z.string().trim().min(2).max(200)
      }),
      ({ args, ctx }) =>
        zb.organizationUsers
          .where('organizationId', args.organizationId)
          .where(eb => isOwnOrSharedOrgUserRow(ctx, eb))
          .whereExists('user', uq =>
            uq.where((eb: any) =>
              eb.or(
                eb.cmp('firstName', 'ILIKE', `%${args.term}%`),
                eb.cmp('lastName', 'ILIKE', `%${args.term}%`),
                // Only a contact the caller may see: an unshared email
                // must not match, or search becomes an email oracle.
                eb.exists('contact', (cq: any) =>
                  cq
                    .where('email', 'ILIKE', `%${args.term}%`)
                    .where((ceb: any) => isVisibleUserContactRow(ctx, ceb))
                )
              )
            )
          )
          .related('user')
          .limit(8)
    )
  },

  inventory: {
    // Replaces SpiritsListTable.tsx's public-catalog query. The org id is
    // fixed server-side, not client-supplied -- nothing to authorize beyond
    // "caller is authenticated" (enforced by the query route rejecting
    // unauthenticated requests before any query.fn runs).
    publicCatalog: defineAppQuery(() =>
      zb.organizations
        .where('id', PUBLIC_ORGANIZATION_ID)
        .related('inventoryItemEdges', eq =>
          eq.related('item', iq => iq.related('categoryEdges'))
        )
        .one()
    ),
    // Replaces SpiritsListTable.tsx's current-org quantity overlay query.
    orgInventory: defineAppQuery(orgIdArgs, ({ args, ctx }) =>
      zb.organizations
        .where('id', args.organizationId)
        .where(eb => isOrgMember(ctx, eb))
        .related('inventoryItemEdges', eq =>
          eq
            .related('item', iq => iq.related('categoryEdges'))
            .related('quantityProperties')
        )
        .one()
    )
  },

  wares: {
    // Retained 86-list ware autocomplete. The edit form is free text now. wares
    // is shared reference data, not org-scoped (see zero-schema.ts's
    // `wares` row select rule -- isAuthenticated only), so no org filter
    // here, matching that same rule.
    search: defineAppQuery(
      z.object({ term: z.string().trim().min(1).max(200) }),
      ({ args }) => zb.wares.where('label', 'ILIKE', `%${args.term}%`).limit(8)
    )
  },

  files: {
    // Backs FileLinkPreviews: resolves same-origin `/api/files/{id}` links
    // pasted into chat. Rows the caller may not read are simply absent, so
    // the link stays plain text with no preview.
    byIds: defineAppQuery(
      z.object({ ids: z.array(idSchema).min(1).max(MAX_FILES_PER_PARENT) }),
      ({ args, ctx }) =>
        zb.files
          .where('id', 'IN', args.ids)
          .where(eb => isVisibleFileRow(ctx, eb))
          .limit(MAX_FILES_PER_PARENT)
    )
  },

  unavailableItems: {
    // Open 86 items: not restored, not deleted. Junction (org -> edge ->
    // unavailableItem), so -- same as inventoryItemEdges/menuEdges above --
    // sorting by createdAt happens client-side, not in the query. Resolved
    // history stays on the server; managers read it through `history`.
    active: defineAppQuery(orgIdArgs, ({ args, ctx }) =>
      zb.organizations
        .where('id', args.organizationId)
        .where(eb => isOwnOrgRow(ctx, eb))
        .related('unavailableItemEdges', eq =>
          eq
            .whereExists('unavailableItem', iq =>
              iq.where('resolvedAt', 'IS', null).where('deletedAt', 'IS', null)
            )
            .related('unavailableItem', iq =>
              iq
                .where('resolvedAt', 'IS', null)
                .where('deletedAt', 'IS', null)
                .related('createdBy', withVisibleContact(ctx))
            )
        )
        .one()
    ),
    // All retained lifecycle states, disclosed only to admins in this org.
    history: defineAppQuery(orgIdArgs, ({ args, ctx }) =>
      zb.organizations
        .where('id', args.organizationId)
        .where(eb => isOwnOrgRow(ctx, eb))
        .where(eb => isEightySixAnalyticsViewer(ctx, eb))
        .related('unavailableItemEdges', eq =>
          eq.related('unavailableItem', iq =>
            iq
              .related('createdBy', withVisibleContact(ctx))
              .related('resolvedBy', withVisibleContact(ctx))
              .related('deletedBy', withVisibleContact(ctx))
          )
        )
        .one()
    )
  },

  searchPreferences: {
    // The caller's own saved default search for this organization; absent
    // means "Recommended" (see EntOrganizationSearchPreference). Never
    // reads another member's row -- see isOwnPreferenceRow.
    current: defineAppQuery(orgIdArgs, ({ args, ctx }) =>
      zb.organizationSearchPreferences
        .where('organizationId', args.organizationId)
        .where(eb => isOwnPreferenceRow(ctx, eb))
        .one()
    )
  },

  knowledgeArticles: {
    // Article detail: published is readable by any org member, draft/
    // archived only by ManageReference holders (isVisibleKnowledgeArticleRow
    // covers both, mirroring EntKnowledgeArticle's Ent privacy).
    byId: defineAppQuery(z.object({ articleId: idSchema }), ({ args, ctx }) =>
      zb.knowledgeArticles
        .where('id', args.articleId)
        .where(eb => isVisibleKnowledgeArticleRow(ctx, eb))
        .one()
    ),
    // Bounded published list for the library/reference surface. Draft and
    // archived rows are excluded from default discovery regardless of the
    // caller's capability (an editor still opens drafts through byId/authoring).
    published: defineAppQuery(orgIdArgs, ({ args, ctx }) =>
      zb.knowledgeArticles
        .where('organizationId', args.organizationId)
        .where('status', 'published')
        .where(eb => isVisibleKnowledgeArticleRow(ctx, eb))
        .orderBy('updatedAt', 'desc')
        .orderBy('id', 'asc')
        .limit(50)
    ),
    // All statuses (draft/published/archived), for the authoring/management
    // list. Row-filtered by the same isVisibleKnowledgeArticleRow check as
    // every other knowledgeArticles query -- a non-privileged caller
    // transparently only ever gets published rows back, the same safety net
    // `published` already relies on.
    manage: defineAppQuery(orgIdArgs, ({ args, ctx }) =>
      zb.knowledgeArticles
        .where('organizationId', args.organizationId)
        .where(eb => isVisibleKnowledgeArticleRow(ctx, eb))
        .limit(200)
    )
  }
});
